import {
  AccountForQrFragment,
  AccountFragment,
  AccountType,
  AccountWithPermissionListForPaymentFragment,
  CardDataForDetailFragment,
  CardDataFragment,
  CardLimit,
  CardLimitInfoFragment,
  CardWithPermissionListFragment,
  ContractTransactionsFragment,
} from '@/app/graphql/account/sdk.account';
import {
  AccountForPayment,
  AccountForQr,
  AccountsWithCurrency,
  AccountTypes,
  BaseRkoAndCardInfo,
  CardAccountForQR,
  CardList,
  Contract,
  Currencies,
} from './type';
import { formatCurrency } from '@/shared/lib/format-currency';
import dayjs from 'dayjs';
import { formatDateView } from '@/shared/lib/date';

export function formatAccounts(accounts: AccountFragment[]) {
  return accounts.reduce((acc: Contract[], item) => {
    const contract = acc.find((contract) => contract.account === item.account);
    if (!contract) {
      acc.push({
        account: item.account,
        contractNumber: item.contractNumber,
        accounts: [item],
        status: item.status,
      });
      return acc;
    }

    contract.accounts.push({ ...item });
    return acc;
  }, []);
}

export function filterAccountsWithPermissionByType(accountsWithPermission: AccountForPayment[]): AccountTypes {
  return filterAccountsByType(accountsWithPermission);
}

export function filterAccountsByType(accounts: AccountForPayment[]) {
  const contracts = formatAccounts(accounts.filter((item) => item.type === AccountType.Rko));
  const cards = accounts.filter((item) => item.type === AccountType.Card);
  const deposits = accounts.filter((item) => item.type === AccountType.Deposit);
  const loan = formatAccounts(accounts.filter((item) => item.type === AccountType.Loan));

  return {
    contracts,
    cards,
    deposits,
    loan,
  };
}

export function formatAccount(account: AccountFragment) {
  return {
    ...account,
    formattedBalance: formatCurrency(account.balance),
  };
}

export function formatAccountsWithCurrency(accounts: AccountForPayment[]) {
  return accounts.reduce((acc: AccountsWithCurrency[], item) => {
    const account = acc.find((contract) => contract.account === item.account);
    if (!account) {
      acc.push({
        account: item.account,
        accountClassification: item.accountClassification,
        currency: [item.currency],
      });
      return acc;
    }

    account.currency.push({ ...item.currency });
    return acc;
  }, []);
}

export class AccountFormatter {
  private accounts: AccountForPayment[];
  constructor(accounts: AccountForPayment[]) {
    this.accounts = accounts;
  }

  sortByBalance() {
    this.accounts = this.accounts.sort((a, b) => {
      return b.balance - a.balance;
    });
    return this;
  }

  sortByAccountType() {
    const sortMap = {
      [AccountType.Rko]: 1,
      [AccountType.Card]: 2,
      [AccountType.Deposit]: 3,
      [AccountType.Loan]: 4,
      [AccountType.Transit]: 5,
    };
    this.accounts = this.accounts.sort((a, b) => {
      return sortMap[a.type] - sortMap[b.type];
    });
    return this;
  }

  disableByBalance(isConversion: boolean = false) {
    this.accounts = this.accounts.map((account) => ({
      ...account,
      disable: isConversion ? !account.balance : false,
    }));
    return this;
  }

  filterByCurrencyKgs() {
    this.accounts = this.accounts.filter((account) => account.currency.isoCode === Currencies.KGS);
    return this;
  }

  format() {
    return this.accounts;
  }
}

export function mapAccount(account: AccountWithPermissionListForPaymentFragment[]): AccountForPayment[] {
  return account.map((item) => {
    return {
      paymentTypes: item.permissions,
      availablePermissions: item.availablePermissions,
      cards: item.cards || [],
      ...item.account,
    };
  });
}

export function mapAccountsToBaseRkoAndCardInfo(accounts: AccountForPayment[]): BaseRkoAndCardInfo[] {
  const getInfo = (account: AccountForPayment, card?: CardWithPermissionListFragment): BaseRkoAndCardInfo => {
    return {
      accountId: account.id,
      account: account.account,
      accountClassification: account.accountClassification,
      cardNum: card?.card?.cardNum || '',
      cardHolderName: card?.card?.cardHolderName || '',
      paymentSystem: card?.card?.paymentSystem,
      balance: account.balance,
      currency: account.currency.isoCode,
      type: account.type,
      disable: !account.balance,
    };
  };

  return accounts.flatMap((account) => {
    if (account.type === AccountType.Rko) return [getInfo(account)];
    if (account.type === AccountType.Card) return account.cards.map((card) => getInfo(account, card));
    return [];
  });
}

export function mapAccountForQr(account: AccountForQrFragment[]): AccountForQr {
  const accountMap = new Map<string, CardAccountForQR>();

  account
    .filter((item) => item.account.type === AccountType.Card)
    .flatMap((item) =>
      item.cards?.map((itemCard) => ({
        cardHolderName: itemCard.card.cardHolderName,
        account: itemCard.card.account,
        cardNum: `...${itemCard.card.cardNum.slice(-4)}`,
        title: itemCard.card.title,
      }))
    )
    .forEach((card) => {
      if (card) {
        const existingCard = accountMap.get(card.account.account);
        if (existingCard) {
          existingCard.cardHolderName += `\n\n${card.cardHolderName}`;
          existingCard.cardNum += `\n\n${card.cardNum}`;
          existingCard.title += `\n\n${card.title}`;
          existingCard.account = card.account;
        } else {
          accountMap.set(card.account.account, { ...card });
        }
      }
    });
  return {
    card: Array.from(accountMap.values()),
    account: account
      .filter((item) => item.account.type === AccountType.Rko)
      .map((item) => {
        return {
          accountClassification: item.account.accountClassification,
          account: item.account.account,
        };
      }),
  };
}

//карты виза бизнес
export function mapCardListOperation(list: ContractTransactionsFragment[]) {
  return list.map((item) => ({
    ...item,
    transactionDate: item.transactionDate ? dayjs(item.transactionDate).format('DD.MM.YYYY. HH:mm') : '',
  }));
}

export function mapCardBalance(card: CardDataForDetailFragment) {
  return {
    ...card,
    multiBalance:
      card.multiBalance.length > 1
        ? [{ amount: card.availableAmount, currency: 'ALL' }, ...card.multiBalance]
        : [...card.multiBalance],
  };
}

export function mapCard(card: CardDataFragment[]): CardList[] {
  return card.flatMap((itemCard) =>
    itemCard.cards.map((item) => ({
      ...item.card,
      expiryDate: item.card.expiryDate?.substring(5)?.replace(/-/g, '/'),
      permission: item.permissions,
      balance:
        itemCard.multiBalance.length > 1
          ? [{ amount: itemCard.availableAmount, currency: 'ALL' }, ...itemCard.multiBalance]
          : [...itemCard.multiBalance],
    }))
  );
}

export function mapCardByAccountId(card: CardDataFragment): CardList[] {
  return card.cards.map((item) => ({
    ...item.card,
    expiryDate: item.card.expiryDate?.substring(5)?.replace(/-/g, '/'),
    permission: item.permissions,
    balance:
      card.multiBalance.length > 1
        ? [{ amount: card.availableAmount, currency: 'ALL' }, ...card.multiBalance]
        : [...card.multiBalance],
  }));
}

export function mapCardLimit(cardLimit: CardLimitInfoFragment[]) {
  const cardLimitOrder: { [key in CardLimit]: number } = {
    [CardLimit.CashLimit]: 1,
    [CardLimit.RetailCardPresent]: 2,
    [CardLimit.CardNotPresent]: 3,
  };

  const sortedCardLimit = cardLimit.sort((a, b) => cardLimitOrder[a.limitType] - cardLimitOrder[b.limitType]);
  return sortedCardLimit.map((item) => ({
    ...item,
    startDate: item.startDate ? formatDateView(item.startDate) : '',
    endDate: item.endDate ? formatDateView(item.endDate) : '',
  }));
}

export function flatAccount(accounts: AccountTypes) {
  return [
    ...accounts.cards.map((card) => {
      return {
        account: card.account,
        accountClassification: card.accountClassification,
      };
    }),
    ...accounts.contracts.flatMap(({ accounts }) =>
      accounts
        .filter(({ currency }) => currency.isoCode === 'KGS')
        .map((item) => {
          return {
            account: item.account,
            accountClassification: item.accountClassification,
          };
        })
    ),
  ];
}
